import { useMemo } from 'react'
import { useAccount, useNetwork } from 'wagmi'
import { getChainId } from 'config/constants/exchange'
import {
  getBep20Contract,
  getCakeContract,
  getBunnyFactoryContract,
  getBunnySpecialContract,
  getPancakeBunniesContract,
  getProfileContract,
  getIfoV1Contract,
  getIfoV2Contract,
  getIfoV3Contract,
  getMasterchefContract,
  getMasterchefV1Contract,
  getPointCenterIfoContract,
  getSouschefContract,
  getClaimRefundContract,
  getTradingCompetitionContractEaster,
  getTradingCompetitionContractFanToken,
  getTradingCompetitionContractMobox,
  getTradingCompetitionContractMoD,
  getEasterNftContract,
  getErc721Contract,
  getCakeVaultV2Contract,
  getPredictionsContract,
  getChainlinkOracleContract,
  getLotteryV2Contract,
  getBunnySpecialCakeVaultContract,
  getBunnySpecialPredictionContract,
  getFarmAuctionContract,
  getBunnySpecialLotteryContract,
  getAnniversaryAchievementContract,
  getNftMarketContract,
  getNftSaleContract,
  getPancakeSquadContract,
  getErc721CollectionContract,
  getBunnySpecialXmasContract,
  getCakeFlexibleSideVaultV2Contract,
  getCakePredictionsContract,
  getPredictionsV1Contract,
} from 'utils/contractHelpers'
import { getMulticallAddress, getPredictionsV1Address, getZapAddress } from 'utils/addressHelpers'
import {
  Erc20,
  Erc20Bytes32,
  Weth,
  Cake,
  Erc721collection,
  CakeVaultV2,
  CakeFlexibleSideVaultV2,
  Zap,
} from 'config/abi/types'
import zapAbi from 'config/abi/zap.json'

// Imports below migrated from Exchange useContract.ts
import { Contract } from '@ethersproject/contracts'
import { bscRpcProvider } from 'utils/providers'
import { ChainId, WNATIVE } from '@pancakeswap/sdk'
import IPancakePairABI from '../config/abi/IPancakePair.json'
import { ERC20_BYTES32_ABI } from '../config/abi/erc20'
import ERC20_ABI from '../config/abi/erc20.json'
import WETH_ABI from '../config/abi/weth.json'
import multiCallAbi from '../config/abi/Multicall.json'
import { getContract, getProviderOrSigner } from '../utils'

import { IPancakePair } from '../config/abi/types/IPancakePair'
import { VaultKey } from '../state/types'
import { useProviderOrSigner } from './useProviderOrSigner'
import { useEthersSigner } from './useEthersProvider'

/**
 * Helper hooks to get specific contracts (by ABI)
 */

export const useIfoV1Contract = (address: string) => {
  const library = useEthersSigner()
  return useMemo(() => getIfoV1Contract(address, library), [address, library])
}

export const useIfoV2Contract = (address: string) => {
  const library = useEthersSigner()
  return useMemo(() => getIfoV2Contract(address, library), [address, library])
}

export const useIfoV3Contract = (address: string) => {
  const library = useEthersSigner()
  return useMemo(() => getIfoV3Contract(address, library), [address, library])
}

export const useERC20 = (address: string, withSignerIfPossible = true) => {
  const { address: account } = useAccount()
  const library = useEthersSigner()
  const signer = useMemo(
    () => (withSignerIfPossible ? getProviderOrSigner(library, account) : null),
    [withSignerIfPossible, library, account],
  )
  return useMemo(() => getBep20Contract(address, signer), [address, signer])
}

/**
 * @see https://docs.openzeppelin.com/contracts/3.x/api/token/erc721
 */
export const useERC721 = (address: string, withSignerIfPossible = true) => {
  const { address: account } = useAccount()
  const library = useEthersSigner()
  const signer = useMemo(
    () => (withSignerIfPossible ? getProviderOrSigner(library, account) : null),
    [withSignerIfPossible, library, account],
  )
  return useMemo(() => getErc721Contract(address, signer), [address, signer])
}

export const useCake = (): { reader: Cake; signer: Cake } => {
  const { address: account } = useAccount()
  const library = useEthersSigner()
  return useMemo(
    () => ({
      reader: getCakeContract(null),
      signer: getCakeContract(getProviderOrSigner(library, account)),
    }),
    [account, library],
  )
}

export const useBunnyFactory = () => {
  const library = useEthersSigner()
  return useMemo(() => getBunnyFactoryContract(library), [library])
}

export const usePancakeBunnies = () => {
  const library = useEthersSigner()
  return useMemo(() => getPancakeBunniesContract(library), [library])
}

export const useProfileContract = (withSignerIfPossible = true) => {
  const library = useEthersSigner()
  const { address: account } = useAccount()
  const signer = useMemo(
    () => (withSignerIfPossible ? getProviderOrSigner(library, account) : null),
    [withSignerIfPossible, library, account],
  )
  return useMemo(() => getProfileContract(signer), [signer])
}

export const useLotteryV2Contract = () => {
  const library = useEthersSigner()
  return useMemo(() => getLotteryV2Contract(library), [library])
}

export const useMasterchef = (withSignerIfPossible = true) => {
  const { chain } = useNetwork()
  const chainId = getChainId(chain);
  const providerOrSigner = useProviderOrSigner(withSignerIfPossible, chainId)
  return useMemo(() => getMasterchefContract(providerOrSigner, chainId), [providerOrSigner, chainId])
}

export const useMasterchefV1 = () => {
  const library = useEthersSigner()
  return useMemo(() => getMasterchefV1Contract(library), [library])
}

export const useSousChef = (id) => {
  const library = useEthersSigner()
  return useMemo(() => getSouschefContract(id, library), [id, library])
}

export const usePointCenterIfoContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getPointCenterIfoContract(library), [library])
}

export const useBunnySpecialContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getBunnySpecialContract(library), [library])
}

export const useClaimRefundContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getClaimRefundContract(library), [library])
}

export const useTradingCompetitionContractEaster = (withSignerIfPossible = true) => {
  const library = useEthersSigner()
  const { address : account } = useAccount()
  const signer = useMemo(
    () => (withSignerIfPossible ? getProviderOrSigner(library, account) : null),
    [withSignerIfPossible, library, account],
  )
  return useMemo(() => getTradingCompetitionContractEaster(signer), [signer])
}

export const useTradingCompetitionContractFanToken = (withSignerIfPossible = true) => {
  const library = useEthersSigner()
  const { address : account } = useAccount()
  const signer = useMemo(
    () => (withSignerIfPossible ? getProviderOrSigner(library, account) : null),
    [withSignerIfPossible, library, account],
  )
  return useMemo(() => getTradingCompetitionContractFanToken(signer), [signer])
}

export const useTradingCompetitionContractMobox = (withSignerIfPossible = true) => {
  const library = useEthersSigner()
  const { address : account } = useAccount()
  const signer = useMemo(
    () => (withSignerIfPossible ? getProviderOrSigner(library, account) : null),
    [withSignerIfPossible, library, account],
  )
  return useMemo(() => getTradingCompetitionContractMobox(signer), [signer])
}

export const useTradingCompetitionContractMoD = (withSignerIfPossible = true) => {
  const library = useEthersSigner()
  const { address : account } = useAccount()
  const signer = useMemo(
    () => (withSignerIfPossible ? getProviderOrSigner(library, account) : null),
    [withSignerIfPossible, library, account],
  )
  return useMemo(() => getTradingCompetitionContractMoD(signer), [signer])
}

export const useEasterNftContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getEasterNftContract(library), [library])
}

export const useVaultPoolContract = (vaultKey: VaultKey): CakeVaultV2 | CakeFlexibleSideVaultV2 => {
  const library = useEthersSigner()
  return useMemo(() => {
    if (vaultKey === VaultKey.CakeVault) {
      return getCakeVaultV2Contract(library)
    }
    if (vaultKey === VaultKey.CakeFlexibleSideVault) {
      return getCakeFlexibleSideVaultV2Contract(library)
    }
    return null
  }, [library, vaultKey])
}

export const useCakeVaultContract = (withSignerIfPossible = true) => {
  const library = useEthersSigner()
  const { address : account } = useAccount()
  const signer = useMemo(
    () => (withSignerIfPossible ? getProviderOrSigner(library, account) : null),
    [withSignerIfPossible, library, account],
  )
  return useMemo(() => getCakeVaultV2Contract(signer), [signer])
}

export const usePredictionsContract = (address: string, tokenSymbol: string) => {
  const library = useEthersSigner()
  return useMemo(() => {
    if (address === getPredictionsV1Address()) {
      return getPredictionsV1Contract(library)
    }
    const getPredContract = tokenSymbol === 'SWAP' ? getCakePredictionsContract : getPredictionsContract

    return getPredContract(address, library)
  }, [library, address, tokenSymbol])
}

export const useChainlinkOracleContract = (address, withSignerIfPossible = true) => {
  const library = useEthersSigner()
  const { address : account } = useAccount()
  const signer = useMemo(
    () => (withSignerIfPossible ? getProviderOrSigner(library, account) : null),
    [withSignerIfPossible, library, account],
  )
  return useMemo(() => getChainlinkOracleContract(address, signer), [signer, address])
}

export const useSpecialBunnyCakeVaultContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getBunnySpecialCakeVaultContract(library), [library])
}

export const useSpecialBunnyPredictionContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getBunnySpecialPredictionContract(library), [library])
}

export const useBunnySpecialLotteryContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getBunnySpecialLotteryContract(library), [library])
}

export const useBunnySpecialXmasContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getBunnySpecialXmasContract(library), [library])
}

export const useAnniversaryAchievementContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getAnniversaryAchievementContract(library), [library])
}

export const useNftSaleContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getNftSaleContract(library), [library])
}

export const usePancakeSquadContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getPancakeSquadContract(library), [library])
}

export const useFarmAuctionContract = (withSignerIfPossible = true) => {
  const { address: account } = useAccount()
  const library = useEthersSigner()
  const signer = useMemo(
    () => (withSignerIfPossible ? getProviderOrSigner(library, account) : null),
    [withSignerIfPossible, library, account],
  )
  return useMemo(() => getFarmAuctionContract(signer), [signer])
}

export const useNftMarketContract = () => {
  const library = useEthersSigner()
  return useMemo(() => getNftMarketContract(library), [library])
}

export const useErc721CollectionContract = (
  collectionAddress: string,
): { reader: Erc721collection; signer: Erc721collection } => {
  const library = useEthersSigner()
  const { address : account } = useAccount()
  return useMemo(
    () => ({
      reader: getErc721CollectionContract(null, collectionAddress),
      signer: getErc721CollectionContract(getProviderOrSigner(library, account), collectionAddress),
    }),
    [account, library, collectionAddress],
  )
}

// Code below migrated from Exchange useContract.ts

// returns null on errors
function useContract<T extends Contract = Contract>(
  address: string | undefined,
  ABI: any,
  withSignerIfPossible = true,
): T | null {
  const { address: account } = useAccount()
  const library = useEthersSigner()
  const { chain } = useNetwork()
  const chainId = getChainId(chain);
  const signer = useMemo(
    () =>
      withSignerIfPossible ? getProviderOrSigner(library, account) : chainId === ChainId.BSC ? bscRpcProvider : library,
    [withSignerIfPossible, library, account, chainId],
  )

  const canReturnContract = useMemo(
    () => address && ABI && (withSignerIfPossible ? library : true),
    [address, ABI, library, withSignerIfPossible],
  )

  return useMemo(() => {
    if (!canReturnContract) return null
    try {
      return getContract(address, ABI, signer)
    } catch (error) {
      console.error('Failed to get contract', error)
      return null
    }
  }, [address, ABI, signer, canReturnContract]) as T
}

export function useTokenContract(tokenAddress?: string, withSignerIfPossible?: boolean) {
  return useContract<Erc20>(tokenAddress, ERC20_ABI, withSignerIfPossible)
}

export function useWCLDContract(withSignerIfPossible?: boolean): Contract | null {
  const { chain } = useNetwork()
  const chainId = getChainId(chain);
  return useContract<Weth>(chainId ? WNATIVE[chainId].address : undefined, WETH_ABI, withSignerIfPossible)
}

export function useBytes32TokenContract(tokenAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract<Erc20Bytes32>(tokenAddress, ERC20_BYTES32_ABI, withSignerIfPossible)
}

export function usePairContract(pairAddress?: string, withSignerIfPossible?: boolean): IPancakePair | null {
  return useContract(pairAddress, IPancakePairABI, withSignerIfPossible)
}

export function useMulticallContract() {
  const { chain } = useNetwork()
  const chainId = getChainId(chain);
  return useContract(getMulticallAddress(chainId), multiCallAbi, false)
}

export function useZapContract(withSignerIfPossible = true) {
  return useContract<Zap>(getZapAddress(), zapAbi, withSignerIfPossible)
}
